import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Download,
  Share2,
  ChevronDown,
  ChevronUp,
  Loader2,
  Sparkles,
  Beaker,
  Leaf,
  Droplets,
  AlertTriangle,
  Info,
  Pencil,
  History,
} from 'lucide-react'
import { useFeatureFlags } from '../contexts/FeatureFlagContext'

interface Recommendation {
  product_slug: string
  product_name: string
  category: string
  application_type: string
  rate: string
  timing: string
  rationale: string
  confidence: number
  talking_points: string[]
}

interface SectionTalkingPoint {
  text: string
  category: string
}

interface Section {
  section_key: string
  section_label: string
  application_type: string
  timing: string
  products: Recommendation[]
  notes: string | null
  talking_points: SectionTalkingPoint[]
}

interface CropResult {
  crop_type: string
  acres: number
  recommendations: Recommendation[]
  sections?: Section[]
  scoring_confidence: number
  dry_fertility?: DryFertilityResult | null
}

interface SoilData {
  pct_fields_buffer_ph_low?: number | null
  buffer_ph?: number | null
  [key: string]: unknown
}

interface DryFertilityResult {
  crop_type: string
  yield_goal: number
  acres: number
  removal_p2o5_lbs_per_acre: number
  removal_k2o_lbs_per_acre: number
  removal_narrative: string
  potash_cost_per_acre: number
  dap_cost_per_acre: number
  total_cost_per_acre: number
  total_cost: number
  savings_narrative: string | null
}

interface RecommendationVersion {
  id: string
  created_at: string | null
  scoring_confidence: number
  crop_count: number
  product_count: number
}

interface RecommendationData {
  session_id: string
  recommendation_id?: string
  crops: CropResult[]
  scoring_confidence: number
  soil_data?: SoilData | null
}

const cropLabels: Record<string, string> = {
  corn: 'Corn',
  soybeans: 'Soybeans',
  wheat: 'Wheat',
  alfalfa: 'Alfalfa',
  potatoes: 'Potatoes',
  sugar_beets: 'Sugar Beets',
  dry_beans: 'Dry Beans',
}

const cropEmojis: Record<string, React.ReactNode> = {
  corn: '🌽',
  soybeans: '🫛',
  wheat: '🌾',
  alfalfa: '🌿',
  potatoes: '🥔',
  sugar_beets: <img src="/sugar-beet.png" alt="Sugar Beet" className="inline-block w-7 h-7" />,
  dry_beans: '🫘',
}

// Category icons
const categoryIcons: Record<string, React.ReactNode> = {
  'starter': <Sparkles className="w-5 h-5" />,
  'micronutrient': <Beaker className="w-5 h-5" />,
  'biological': <Leaf className="w-5 h-5" />,
  'foliar': <Droplets className="w-5 h-5" />,
}

export default function Results() {
  const { sessionId } = useParams()
  const navigate = useNavigate()
  const { kamariMode } = useFeatureFlags()
  const [data, setData] = useState<RecommendationData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expandedProduct, setExpandedProduct] = useState<string | null>(null)
  const [downloading, setDownloading] = useState(false)
  const [activeCropIndex, setActiveCropIndex] = useState(0)
  const [versions, setVersions] = useState<RecommendationVersion[]>([])
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null)

  useEffect(() => {
    async function fetchResults() {
      try {
        // Fetch versions list and current recommendation in parallel
        const [recRes, versionsRes] = await Promise.all([
          fetch(`/api/sessions/${sessionId}/recommendation`),
          fetch(`/api/sessions/${sessionId}/recommendations`),
        ])
        if (!recRes.ok) throw new Error('Failed to fetch recommendations')
        const json = await recRes.json()
        setData(json)
        if (json.recommendation_id) setSelectedVersionId(json.recommendation_id)

        if (versionsRes.ok) {
          const vList = await versionsRes.json()
          setVersions(vList)
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred')
      } finally {
        setLoading(false)
      }
    }

    if (sessionId) {
      fetchResults()
    }
  }, [sessionId])

  const handleVersionChange = async (versionId: string) => {
    setSelectedVersionId(versionId)
    setLoading(true)
    try {
      const res = await fetch(
        `/api/sessions/${sessionId}/recommendation?recommendation_id=${versionId}`
      )
      if (!res.ok) throw new Error('Failed to fetch recommendation version')
      const json = await res.json()
      setData(json)
      setActiveCropIndex(0)
      setExpandedProduct(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setLoading(false)
    }
  }

  const handleDownloadPDF = async () => {
    setDownloading(true)
    try {
      const res = await fetch(`/api/sessions/${sessionId}/pdf`)
      if (!res.ok) throw new Error('Failed to generate PDF')
      const blob = new Blob([await res.arrayBuffer()], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `recommendation-${sessionId}.pdf`
      a.style.display = 'none'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Error downloading PDF:', err)
    } finally {
      setDownloading(false)
    }
  }

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Product Recommendation',
          text: 'Check out this product recommendation from Yield Innovations',
          url: window.location.href,
        })
      } catch {
        // Share cancelled
      }
    }
  }

  if (loading) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-white">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-yi-green spinner mx-auto mb-4" />
          <p className="text-gray-600">Loading recommendations...</p>
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-white p-6">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error || 'No data found'}</p>
          <button
            onClick={() => navigate('/')}
            className="text-yi-green font-medium"
          >
            Return Home
          </button>
        </div>
      </div>
    )
  }

  const crops = data.crops || []
  const activeCrop = crops[activeCropIndex]

  // Use sections if available, otherwise fall back to grouping by application_type
  const sections = activeCrop?.sections
  const groupedRecs = !sections
    ? (activeCrop?.recommendations || []).reduce((acc, rec) => {
        const key = rec.application_type
        if (!acc[key]) acc[key] = []
        acc[key].push(rec)
        return acc
      }, {} as Record<string, Recommendation[]>)
    : null

  const slotLabels: Record<string, string> = {
    'in-furrow': 'In-Furrow',
    '2x2': '2x2',
    'sidedress': 'Sidedress',
    'foliar': 'Foliar',
    'hopper': 'Hopper Treatment',
    'adjuvant': 'Adjuvant',
  }

  const totalRecs = crops.reduce((sum, c) => sum + (c.recommendations?.length || 0), 0)

  return (
    <div className="min-h-dvh flex flex-col bg-gray-50 safe-top safe-bottom">
      {/* Header */}
      <div className="sticky top-0 bg-white z-10 border-b border-gray-100">
        <div className="flex items-center justify-between p-4">
          <button
            onClick={() => navigate('/')}
            className="p-2 -ml-2 text-gray-600 btn-press"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <img src="/yieldos-logo.jpg" alt="YieldOS" className="h-8" />
          <button
            onClick={handleShare}
            className="p-2 -mr-2 text-gray-600 btn-press"
          >
            <Share2 className="w-5 h-5" />
          </button>
        </div>

        {/* Crop tabs */}
        {crops.length > 1 && (
          <div className="flex gap-1 px-4 pb-3 overflow-x-auto">
            {crops.map((crop, idx) => (
              <button
                key={crop.crop_type}
                onClick={() => { setActiveCropIndex(idx); setExpandedProduct(null) }}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap
                  transition-colors
                  ${idx === activeCropIndex
                    ? 'bg-yi-green text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
              >
                <span>{cropEmojis[crop.crop_type] || ''}</span>
                <span>{cropLabels[crop.crop_type] || crop.crop_type}</span>
                <span className="opacity-70">{crop.acres}ac</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Version Selector */}
      {kamariMode && versions.length > 1 && (
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-100 flex items-center gap-3">
          <History className="w-4 h-4 text-gray-400" />
          <label className="text-sm text-gray-500">Version:</label>
          <select
            value={selectedVersionId ?? ''}
            onChange={(e) => handleVersionChange(e.target.value)}
            className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white focus:ring-2 focus:ring-yi-green/20 focus:border-yi-green"
          >
            {versions.map((v, i) => (
              <option key={v.id} value={v.id}>
                v{versions.length - i} — {v.crop_count} crop{v.crop_count !== 1 ? 's' : ''}, {v.product_count} product{v.product_count !== 1 ? 's' : ''} — {Math.round(v.scoring_confidence * 100)}%
                {v.created_at ? ` — ${new Date(v.created_at).toLocaleDateString()}` : ''}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Confidence Banner */}
      <div className="bg-yi-green text-white px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm opacity-80">Match Confidence</p>
            <p className="text-2xl font-bold">
              {Math.round((activeCrop?.scoring_confidence || data.scoring_confidence) * 100)}%
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm opacity-80">Products</p>
            <p className="text-2xl font-bold">
              {activeCrop?.recommendations?.length || 0}
            </p>
          </div>
          {crops.length > 1 && (
            <div className="text-right">
              <p className="text-sm opacity-80">Total (all crops)</p>
              <p className="text-2xl font-bold">{totalRecs}</p>
            </div>
          )}
        </div>
        {crops.length === 1 && activeCrop && (
          <p className="text-sm opacity-80 mt-1">
            {cropEmojis[activeCrop.crop_type]} {cropLabels[activeCrop.crop_type] || activeCrop.crop_type} &mdash; {activeCrop.acres} acres
          </p>
        )}
      </div>

      {/* Kamari mode banners */}
      {kamariMode && (
        <div className="px-4 pt-4 space-y-3">
          {/* Lime Advisory — when soil data shows low buffer pH */}
          {data.soil_data && (data.soil_data.pct_fields_buffer_ph_low ?? 0) > 0 && (
            <div className="flex items-start gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-amber-800">Lime Advisory</p>
                <p className="text-sm text-amber-700">
                  {data.soil_data.pct_fields_buffer_ph_low}% of your acres have buffer pH at or below 6.8 — these acres need lime.
                </p>
              </div>
            </div>
          )}

          {/* No soil data notice */}
          {!data.soil_data && (
            <div className="flex items-start gap-3 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl">
              <Info className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-gray-500">
                Recommendations generated without soil test data.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Recommendations List */}
      <div className="flex-1 p-4 space-y-6">
        {sections ? (
          /* Section-based rendering */
          sections.map(section => (
            <div key={section.section_key}>
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1 px-2">
                {section.section_label}
              </h2>
              <p className="text-xs text-gray-400 mb-3 px-2">{section.timing}</p>

              {/* Section-level talking points (synergies) */}
              {section.talking_points.length > 0 && (
                <div className="mx-2 mb-3 px-3 py-2 bg-purple-50 rounded-lg border border-purple-100">
                  <p className="text-xs font-semibold text-purple-700 mb-1">Consultant Notes</p>
                  {section.talking_points.map((tp, i) => (
                    <p key={i} className="text-xs text-purple-600 italic">{tp.text}</p>
                  ))}
                </div>
              )}

              <div className="space-y-3">
                {section.products.map(rec => {
                  const cardKey = `${section.section_key}-${rec.product_slug}`
                  return (
                    <ProductCard
                      key={cardKey}
                      recommendation={rec}
                      expanded={expandedProduct === cardKey}
                      onToggle={() => setExpandedProduct(
                        expandedProduct === cardKey ? null : cardKey
                      )}
                    />
                  )
                })}
              </div>
            </div>
          ))
        ) : groupedRecs ? (
          /* Legacy flat grouping */
          Object.entries(groupedRecs).map(([slot, recs]) => (
            <div key={slot}>
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 px-2">
                {slotLabels[slot] || slot}
              </h2>
              <div className="space-y-3">
                {recs.map(rec => (
                  <ProductCard
                    key={rec.product_slug}
                    recommendation={rec}
                    expanded={expandedProduct === rec.product_slug}
                    onToggle={() => setExpandedProduct(
                      expandedProduct === rec.product_slug ? null : rec.product_slug
                    )}
                  />
                ))}
              </div>
            </div>
          ))
        ) : null}

        {(activeCrop?.recommendations?.length || 0) === 0 && (
          <div className="text-center py-12 text-gray-500">
            No recommendations for this crop yet.
          </div>
        )}

        {/* Dry Fertility Analysis — kamari mode only */}
        {kamariMode && activeCrop?.dry_fertility && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3">
            <h3 className="text-sm font-semibold text-blue-800 uppercase tracking-wide">
              Dry Fertility Analysis
            </h3>
            <p className="text-sm text-blue-700">
              {activeCrop.dry_fertility.removal_narrative}
            </p>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-white/60 rounded-lg p-3">
                <p className="text-xs text-blue-500">DAP Cost</p>
                <p className="font-semibold text-blue-900">
                  ${activeCrop.dry_fertility.dap_cost_per_acre.toFixed(2)}/A
                </p>
              </div>
              <div className="bg-white/60 rounded-lg p-3">
                <p className="text-xs text-blue-500">Potash Cost</p>
                <p className="font-semibold text-blue-900">
                  ${activeCrop.dry_fertility.potash_cost_per_acre.toFixed(2)}/A
                </p>
              </div>
              <div className="bg-white/60 rounded-lg p-3">
                <p className="text-xs text-blue-500">Total per Acre</p>
                <p className="font-semibold text-blue-900">
                  ${activeCrop.dry_fertility.total_cost_per_acre.toFixed(2)}/A
                </p>
              </div>
              <div className="bg-white/60 rounded-lg p-3">
                <p className="text-xs text-blue-500">Total ({activeCrop.dry_fertility.acres} ac)</p>
                <p className="font-semibold text-blue-900">
                  ${activeCrop.dry_fertility.total_cost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
            </div>
            {activeCrop.dry_fertility.savings_narrative && (
              <p className="text-sm text-blue-600 italic">
                {activeCrop.dry_fertility.savings_narrative}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="sticky bottom-0 pt-4 px-6 pb-6 bg-white border-t border-gray-100 safe-bottom space-y-3">
        <button
          onClick={handleDownloadPDF}
          disabled={downloading}
          className="w-full bg-yi-green text-white rounded-xl py-4 px-6 font-semibold
                     flex items-center justify-center gap-2 btn-press
                     disabled:opacity-50"
        >
          {downloading ? (
            <>
              <Loader2 className="w-5 h-5 spinner" />
              <span>Generating PDF...</span>
            </>
          ) : (
            <>
              <Download className="w-5 h-5" />
              <span>Download Rec Sheet</span>
            </>
          )}
        </button>
        {kamariMode && (
          <button
            onClick={() => navigate(`/questionnaire/${sessionId}`)}
            className="w-full bg-white text-yi-green border-2 border-yi-green rounded-xl py-3.5 px-6 font-semibold
                       flex items-center justify-center gap-2 btn-press
                       hover:bg-yi-green/5 transition-colors"
          >
            <Pencil className="w-4 h-4" />
            <span>Edit Inputs & Re-run</span>
          </button>
        )}
      </div>
    </div>
  )
}

function ProductCard({
  recommendation,
  expanded,
  onToggle
}: {
  recommendation: Recommendation
  expanded: boolean
  onToggle: () => void
}) {
  const confidenceColor = recommendation.confidence >= 0.7
    ? 'bg-green-100 text-green-800'
    : recommendation.confidence >= 0.4
    ? 'bg-yellow-100 text-yellow-800'
    : 'bg-gray-100 text-gray-800'

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full p-4 text-left btn-press"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-yi-green/10 text-yi-green
                          flex items-center justify-center flex-shrink-0">
            {categoryIcons[recommendation.category] || <Beaker className="w-5 h-5" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-gray-900 truncate">
                {recommendation.product_name}
              </h3>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${confidenceColor}`}>
                {Math.round(recommendation.confidence * 100)}%
              </span>
            </div>
            <p className="text-sm text-gray-500">
              {recommendation.rate} · {recommendation.timing}
            </p>
          </div>
          <div className="text-gray-400 flex-shrink-0">
            {expanded ? (
              <ChevronUp className="w-5 h-5" />
            ) : (
              <ChevronDown className="w-5 h-5" />
            )}
          </div>
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 pt-2 border-t border-gray-50 animate-slide-up">
          <div className="mb-4">
            <p className="text-sm font-medium text-gray-700 mb-1">Why this product?</p>
            <p className="text-sm text-gray-600">{recommendation.rationale}</p>
          </div>

          {recommendation.talking_points.length > 0 && (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Talking Points</p>
              <ul className="space-y-2">
                {recommendation.talking_points.map((point, i) => (
                  <li key={i} className="flex gap-2 text-sm text-gray-600">
                    <span className="text-yi-green flex-shrink-0">•</span>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
